import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiErrorMessage } from '../utils/apiError';

interface RegisterProps {
  onSwitchToLogin: () => void;
}

const Register: React.FC<RegisterProps> = ({ onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'patient' as 'admin' | 'member' | 'patient',
    specialty: '',
    license_number: '',
    phone: '',
    date_of_birth: '',
    clinician_email: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const registerData = {
        ...formData,
        specialty: formData.role === 'member' ? formData.specialty : undefined,
        license_number: formData.role === 'member' ? formData.license_number : undefined,
        phone: formData.role === 'patient' ? formData.phone : undefined,
        date_of_birth: formData.role === 'patient' && formData.date_of_birth ? new Date(formData.date_of_birth).toISOString() : undefined,
        clinician_email: formData.role === 'patient' ? formData.clinician_email : undefined,
      };
      
      await register(registerData);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Registration failed. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const isMember = formData.role === 'member';
  const isPatient = formData.role === 'patient';

  return (
    <div className="min-h-screen flex items-center justify-center bg-clinic-surface py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-clinic-dark font-body">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-clinic-muted">
            Join the Clinic Room Booking System
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-clinic-accent/10 border border-clinic-accent/30 text-clinic-accent px-4 py-3 rounded-input">
              {error}
            </div>
          )}
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-clinic-dark">
                  First Name
                </label>
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  required
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-clinic-primary-light placeholder-clinic-muted text-clinic-dark rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary sm:text-sm"
                  placeholder="First Name"
                  value={formData.first_name}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-clinic-dark">
                  Last Name
                </label>
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  required
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-clinic-primary-light placeholder-clinic-muted text-clinic-dark rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary sm:text-sm"
                  placeholder="Last Name"
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-clinic-dark">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-clinic-primary-light placeholder-clinic-muted text-clinic-dark rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary sm:text-sm"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-clinic-dark">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-clinic-primary-light placeholder-clinic-muted text-clinic-dark rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary sm:text-sm"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-clinic-dark">
                Role
              </label>
              <select
                id="role"
                name="role"
                required
                className="mt-1 block w-full px-3 py-2 border border-clinic-primary-light bg-white rounded-input shadow-sm focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary sm:text-sm"
                value={formData.role}
                onChange={handleChange}
              >
                <option value="patient">Client</option>
                <option value="member">Member (Clinician)</option>
              </select>
            </div>

            {isMember && (
              <>
                <div>
                  <label htmlFor="specialty" className="block text-sm font-medium text-clinic-dark">
                    Specialty
                  </label>
                  <input
                    id="specialty"
                    name="specialty"
                    type="text"
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-clinic-primary-light placeholder-clinic-muted text-clinic-dark rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary sm:text-sm"
                    placeholder="e.g., Cardiology, Pediatrics"
                    value={formData.specialty}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label htmlFor="license_number" className="block text-sm font-medium text-clinic-dark">
                    License Number
                  </label>
                  <input
                    id="license_number"
                    name="license_number"
                    type="text"
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-clinic-primary-light placeholder-clinic-muted text-clinic-dark rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary sm:text-sm"
                    placeholder="Medical License Number"
                    value={formData.license_number}
                    onChange={handleChange}
                  />
                </div>
              </>
            )}

            {isPatient && (
              <>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-clinic-dark">
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-clinic-primary-light placeholder-clinic-muted text-clinic-dark rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary sm:text-sm"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                
                <div>
                  <label htmlFor="date_of_birth" className="block text-sm font-medium text-clinic-dark">
                    Date of Birth
                  </label>
                  <input
                    id="date_of_birth"
                    name="date_of_birth"
                    type="date"
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-clinic-primary-light placeholder-clinic-muted text-clinic-dark rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary sm:text-sm"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                  />
                </div>
                
                <div>
                  <label htmlFor="clinician_email" className="block text-sm font-medium text-clinic-dark">
                    Clinician Email Address
                  </label>
                  <input
                    id="clinician_email"
                    name="clinician_email"
                    type="email"
                    required
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-clinic-primary-light placeholder-clinic-muted text-clinic-dark rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary sm:text-sm"
                    placeholder="Enter your assigned clinician's email"
                    value={formData.clinician_email}
                    onChange={handleChange}
                  />
                  <p className="mt-1 text-xs text-clinic-muted">
                    Enter the email address of the clinician assigned to you
                  </p>
                </div>
              </>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-button text-clinic-cream bg-clinic-primary shadow-clinic-sm hover:bg-clinic-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-clinic-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="text-sm font-medium text-clinic-primary hover:text-clinic-primary-dark"
            >
              Already have an account? Sign in here
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;